import { useCallback, useContext } from 'react';

import {
  ADD_APP_PAGE,
  APP_BUILDER_BROWSE_TAB_PATH,
  SETUP_APP_PAGE_BASE_PATH,
} from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import { UrlUtils } from 'src/utils';

interface SetupAppPageUrlParams {
  moduleType: string;
  moduleId: string;
  preset?: string;
  appId?: string;
  /**
   * When set, records the page the user is currently on (`from` query param) so
   * the setup page can offer an accurate "Add an App" back crumb. Use this for
   * the add/install flow, not when editing an already-installed app.
   */
  fromCurrentPage?: boolean;
}

export const useAppSetupPaths = () => {
  const { pathname, query } = useContext(RouteContext);
  const { folderId } = query as { folderId?: string };
  const from = query.from ?? '';
  const { EnableAppSetupImprovements } = useFeatureFlags();

  const addAppPagePath = folderId
    ? `${ADD_APP_PAGE.path}?folderId=${folderId}`
    : ADD_APP_PAGE.path;

  const isInternalFrom = from.startsWith('/') && !from.startsWith('//');
  const cameFromAddApp = EnableAppSetupImprovements && isInternalFrom;

  // Canonical Browse path, preserving the folder context when one is present.
  // The base path already carries a query string, so `folderId` is appended
  // with `&`.
  const browseReturnPath = folderId
    ? `${APP_BUILDER_BROWSE_TAB_PATH}&folderId=${folderId}`
    : APP_BUILDER_BROWSE_TAB_PATH;

  // Where the add/embed setup flow returns to on cancel. In the redesigned flow
  // that's the Browse app: normally the threaded origin (which is the Browse
  // page, carrying its folder/tab context), falling back to the canonical
  // Browse path when no origin was threaded. The flag-off flow keeps returning
  // to the legacy "Add an App" page.
  const addAppReturnPath = cameFromAddApp
    ? from
    : EnableAppSetupImprovements
      ? browseReturnPath
      : addAppPagePath;

  const getAppDetailsPagePath = useCallback(
    (slug: string) => {
      const baseUrl = `/app-setup/${slug}`;
      return folderId ? `${baseUrl}?folderId=${folderId}` : baseUrl;
    },
    [folderId],
  );

  const getSetupAppPagePath = useCallback(
    ({
      moduleType,
      moduleId,
      preset,
      appId,
      fromCurrentPage,
    }: SetupAppPageUrlParams) => {
      const params = new URLSearchParams({ moduleType, moduleId });
      if (preset) {
        params.set('preset', preset);
      }
      if (appId) {
        params.set('appId', appId);
      }
      if (folderId) {
        params.set('folderId', folderId);
      }
      if (fromCurrentPage) {
        const queryString = UrlUtils.FormatUrlQueryToString(query);
        params.set(
          'from',
          queryString ? `${pathname}?${queryString}` : pathname,
        );
      }

      return `${SETUP_APP_PAGE_BASE_PATH}?${params.toString()}`;
    },
    [folderId, pathname, query],
  );

  return {
    /** folderId from the URL query param */
    folderId,
    /** path to the (legacy) standalone add app page */
    addAppPagePath,
    /** whether the setup flow was launched from the redesigned Add App flow */
    cameFromAddApp,
    /** where to send the user when they cancel/leave the add or embed flow */
    addAppReturnPath,
    /** builds the path to the app details page for a given slug */
    getAppDetailsPagePath,
    /** builds the full path to the setup app page */
    getSetupAppPagePath,
  } as const;
};
